import { useUserRole } from "$/hooks/use-user-role"
import { onboardingSections } from "$/lib/onboarding"
import { OnboardingSection, OnboardingVideo } from "$/lib/onboarding/types"
import {
  OnboardingWatchedStatus,
  Route,
  SetOnboardingWatched,
  Translator,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { useTranslations } from "use-intl"
import { request } from "../api"

export interface ResolvedOnboardingVideo
  extends Omit<OnboardingVideo, "titleKey"> {
  title: string
  isWatched: boolean
}

export interface ResolvedOnboardingSection {
  id: string
  title: string
  videos: ResolvedOnboardingVideo[]
}

async function fetchWatchedStatuses(
  storeId: string,
): Promise<OnboardingWatchedStatus[]> {
  return request({
    url: populateRoute(Route.ONBOARDING__WATCHED_STATUSES, storeId),
    method: "GET",
  })
}

async function setWatched(
  storeId: string,
  slug: string,
  data: SetOnboardingWatched,
): Promise<void> {
  return request({
    url: populateRoute(Route.ONBOARDING__SET_WATCHED, storeId, slug),
    method: "PATCH",
    data,
  })
}

const onboardingQueries = {
  all: () => ["onboarding"],
  watchedStatuses: (storeId: string) =>
    queryOptions({
      queryKey: [...onboardingQueries.all(), "watched-statuses", storeId],
      queryFn: () => fetchWatchedStatuses(storeId),
      staleTime: 5000,
    }),
}

const visibleSections = (isOwner: boolean): OnboardingSection[] =>
  onboardingSections.filter((section) => isOwner || !section.ownerOnly)

const resolveSections = (
  watchedStatuses: OnboardingWatchedStatus[] | undefined,
  isOwner: boolean,
  t: Translator,
): ResolvedOnboardingSection[] => {
  const watchedSlugs = new Set(
    (watchedStatuses ?? []).map((status) => status.slug),
  )
  return visibleSections(isOwner).map((section) => ({
    id: section.id,
    title: t(section.titleKey),
    videos: section.videos.map((video) => ({
      slug: video.slug,
      youtubeId: video.youtubeId,
      title: t(video.titleKey),
      isWatched: watchedSlugs.has(video.slug),
    })),
  }))
}

const countUnwatched = (sections: ResolvedOnboardingSection[]): number =>
  sections.reduce(
    (total, section) =>
      total + section.videos.filter((video) => !video.isWatched).length,
    0,
  )

const totalVisibleVideos = (isOwner: boolean): number =>
  visibleSections(isOwner).reduce(
    (total, section) => total + section.videos.length,
    0,
  )

export const useOnboardingSections = (storeId: string) => {
  const { isOwner } = useUserRole()
  const t = useTranslations()
  const { data, ...rest } = useQuery(onboardingQueries.watchedStatuses(storeId))
  const sections = resolveSections(data, isOwner, t)
  return {
    sections,
    totalCount: totalVisibleVideos(isOwner),
    unwatchedCount: countUnwatched(sections),
    ...rest,
  }
}

export const useOnboardingUnwatchedCount = (storeId: string) => {
  const { isOwner } = useUserRole()
  const t = useTranslations()
  const { data } = useQuery(onboardingQueries.watchedStatuses(storeId))
  return countUnwatched(resolveSections(data, isOwner, t))
}

export const useSetOnboardingWatched = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ slug, isWatched }: { slug: string; isWatched: boolean }) =>
      setWatched(storeId, slug, { isWatched }),
    onSettled: () => {
      queryClient.invalidateQueries(onboardingQueries.watchedStatuses(storeId))
    },
  })
}

export default onboardingQueries
